import React, { useEffect } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import Timeline from '@material-ui/lab/Timeline';

import { loadApplicantAutomations } from '../../applicant-automations/actions/load-applicant-automations';
import {
  applicantAutomationsForOrgIdSelector,
  applicantAutomationsStateForOrgIdSelector,
} from '../../applicant-automations/applicant-automation-selectors';

import { AutomationSectionEmptyState } from './widgets/automation-section-empty-state';
import { AutomationTimelineTitleCreate } from './widgets/automation-timeline-title-create';
import { AutomationTimelineTriggerSection } from './widgets/automation-timeline-trigger-section';
import { LoadingMessage } from '../../widgets/loading-message';
import {
  getOrderedApplicantAutomationTriggersBySection,
  getParentAutomationsByTriggerForSection,
  groupAutomationsByTitleForSection,
} from '../utils/applicant-automation-display-utils';

const mapStateToProps = (state: any, { org: { org_id: orgId } }: InputProps) => ({
  automations: applicantAutomationsForOrgIdSelector(state, orgId),
  automationsState: applicantAutomationsStateForOrgIdSelector(state, orgId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { org: { org_id: orgId } }: InputProps,
) => ({
  actions: {
    loadApplicantAutomationsByOrg: () => dispatch(loadApplicantAutomations({ orgId })),
  },
});
type InputProps = {
  org: Org;
  editingForJob?: Job;
  focusApplicantAutomation?: ApplicantAutomation | null;
};
type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

export const HiringAutomations = connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  ({
    actions,
    org,
    editingForJob,
    focusApplicantAutomation,
    automations,
    automationsState,
  }: Props) => {
    useEffect(() => {
      actions.loadApplicantAutomationsByOrg();
    });

    if (!automationsState || automationsState.loading) {
      return <LoadingMessage />;
    }

    const section = 'hiring';
    if (!automations || !automations.length) {
      return <AutomationSectionEmptyState section={section} org={org} job={editingForJob} />;
    }

    const hiringAutomations = groupAutomationsByTitleForSection(
      section,
      automations,
      org.org_id,
      editingForJob,
    );

    const orderedPresentTriggers = getOrderedApplicantAutomationTriggersBySection(section).filter(
      (trigger) => hiringAutomations[trigger],
    );

    const hiringParentAutomations = getParentAutomationsByTriggerForSection(
      orderedPresentTriggers,
      hiringAutomations,
    );

    return (
      <Timeline className="u-display-flex u-flexDirection-column u-width-100">
        {orderedPresentTriggers.map(
          (trigger, index) =>
            hiringAutomations[trigger]?.title && (
              <AutomationTimelineTriggerSection
                key={`${section}-${hiringAutomations[trigger]?.title}`}
                org={org}
                editingForJob={editingForJob}
                trigger={trigger}
                title={hiringAutomations[trigger]?.title}
                section={section}
                isFirst={index === 0}
                automations={hiringAutomations[trigger]?.automations}
                parentAutomationsForSection={hiringParentAutomations}
                focusApplicantAutomation={focusApplicantAutomation}
              />
            ),
        )}
        <AutomationTimelineTitleCreate
          org={org}
          triggerType="applicant"
          section={section}
          editingForJob={editingForJob}
          parentAutomationsForSection={hiringParentAutomations}
          isLast
        />
      </Timeline>
    );
  },
);
